import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';

import './desktop-detail-add-to-cart-button-style.scss';
import { CredentialContext, credentialType } from '@providers';
import { useSnackbar } from '@src/providers/SnackbarProvider';
import useIsComDomain from '@src/hooks/useIsComDomain';

import Button from '@src/componentsNew/Buttons/Button';
import OTPVerificationModal from '@src/componentsNew/Modals/OTPVerificationModal/OTPVerificationModal';

// Add To Cart 쿼리
import { CARTLINES_ADD } from '@components/ProductPage/ProductDetailSummary/queries';
import { cartLinesAdd } from '@components/ProductPage/ProductDetailSummary/__generated__/cartLinesAdd';
import {
  product_product,
  product_product_variants,
} from '@components/ProductPage/ProductDetailPage/__generated__/product';

// Nofify 쿼리
import { NOTIFY_ME_CREATE } from '@molecules/NotifyMeModal/queries';
import { notifyMeCreate } from '@molecules/NotifyMeModal/__generated__/notifyMeCreate';

import useScroll from '@src/hooks/useScroll';
import { getDBIdFromGraphqlId } from '@src/core/utils';
import {
  getAddToCartVariables,
  addToCartMutationSuccessCallback,
  StateOfVariantForAddToCart,
} from '@src/utils/addToCart';
import { RESOLVE_ERROR_MESSAGE } from '@src/core/config';
import { cartUrl } from '@src/app/routes';

interface IProps {
  isThumbnail?: boolean;
  product: product_product;
  selectedVariant?: product_product_variants | null;
  refetch?: any;
  showVariants: boolean;
}

const DesktopDetailAddToCartButton = ({
  isThumbnail,
  product,
  selectedVariant = null,
  refetch,
  showVariants,
}: IProps) => {
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const isComDomain = useIsComDomain();
  const { scrollTop } = useScroll();
  const { currentUser, currentCart, setCurrentCart, createAndSetAnonymousCart } = useContext<credentialType>(
    CredentialContext,
  );

  const {
    variants,
    productType: { id: productTypeGraphqlId },
  } = product;
  const productTypeId = getDBIdFromGraphqlId(productTypeGraphqlId);

  const [cartLinesAdd, { loading: cartLinesAddLoading }] = useMutation<cartLinesAdd>(CARTLINES_ADD);
  const [createNotifyMe, { loading: createNotifyMeLoading }] = useMutation<notifyMeCreate>(NOTIFY_ME_CREATE, {
    onCompleted: (data) => {
      showSnackbar({ message: 'Notify me request complete' });

      refetch();
    },
  });

  const [stateOfVariant, setStateOfVariant] = useState(
    StateOfVariantForAddToCart(showVariants ? selectedVariant : variants[0]),
  );
  const [isOTPVerificationModalOpen, setIsOTPVerificationModalOpen] = useState(false);

  useEffect(() => {
    if (showVariants) {
      setStateOfVariant(StateOfVariantForAddToCart(selectedVariant));
    }
  }, [selectedVariant]);

  const handleOTPVerificationModalOpen = () => {
    setIsOTPVerificationModalOpen(true);
  };

  const handleOTPVerificationModalClose = (e) => {
    e.stopPropagation();
    setIsOTPVerificationModalOpen(false);
  };

  const addToCartByToken = async (
    productTypeIsMgs: boolean,
    token: string,
    variant: product_product_variants,
    quantity: number = 1,
    isBuyNow: boolean,
  ) => {
    const variantId = variant.id;
    const variables = getAddToCartVariables(token, variantId, quantity);

    const cartLinesAddResult = await cartLinesAdd({ variables });
    const data = cartLinesAddResult?.data?.cartLineAdd;

    const cart = data?.cart;
    const errors = data?.errors?.filter((error) => error.message.includes(RESOLVE_ERROR_MESSAGE));

    if (errors?.length) {
      const cartCreateVariables = Object.assign(variables, { forMgs: productTypeIsMgs });
      const cartCreateDataCart = await createAndSetAnonymousCart(cartCreateVariables, false);

      if (cartCreateDataCart?.token) {
        setCurrentCart(cartCreateDataCart);

        if (isBuyNow) {
          history.push(cartUrl);
          return;
        }

        showSnackbar({
          message: 'Product Added to Bag',
          isShowBtn: true,
          btnContent: 'Bag',
          btnOnClick: () => history.push(cartUrl),
        });
      }
    } else {
      if (cart) {
        const previousPage = history.location.pathname;

        addToCartMutationSuccessCallback(cartLinesAddResult, product, variant, previousPage);
        setCurrentCart(cart);

        if (isBuyNow) {
          history.push(cartUrl);
          return;
        }

        showSnackbar({
          message: 'Product Added to Bag',
          isShowBtn: true,
          btnContent: 'Bag',
          btnOnClick: () => history.push(cartUrl),
        });
      }
    }
  };

  const handleClickAddToCartButton = (e: React.MouseEvent<HTMLButtonElement>, isBuyNow: boolean) => {
    e.stopPropagation();

    const productTypeIsMgs = product?.productType?.isMgs;
    const currentTypeCartToken = productTypeIsMgs ? currentCart?.mgsCart?.token : currentCart?.maccaronCart?.token;

    if (showVariants) {
      if (selectedVariant) {
        addToCartByToken(productTypeIsMgs, currentTypeCartToken, selectedVariant, 1, isBuyNow);
      } else {
        if (isThumbnail) {
          scrollTop();
        }

        showSnackbar({ message: 'Please Select Variant' });
      }
    } else {
      addToCartByToken(productTypeIsMgs, currentTypeCartToken, variants[0], 1, isBuyNow);
    }
  };

  const handleClickNotifyMeButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!currentUser?.token) {
      showSnackbar({ message: 'You should login' });

      return;
    }

    if (currentUser?.user?.mobileNumber?.toString() !== '9999999999' && currentUser?.user?.mobileVerified) {
      if (showVariants) {
        createNotifyMe({ variables: { variantId: selectedVariant.id } });
      } else {
        createNotifyMe({ variables: { variantId: product.variants[0].id } });
      }
    } else {
      handleOTPVerificationModalOpen();
    }
  };

  return (
    <div className="desktop-detail-add-to-cart-btn-container">
      {stateOfVariant === 'discontinued' && (
        <Button size={isThumbnail ? 'medium' : 'large'} cName="add-to-cart-btn" variant="outlined" disabled={true}>
          Discontinued
        </Button>
      )}
      {stateOfVariant === 'notifyRequested' && (
        <Button size={isThumbnail ? 'medium' : 'large'} cName="add-to-cart-btn" variant="outlined" disabled={true}>
          Notify Me Requested
        </Button>
      )}
      {stateOfVariant === 'notifyMe' && (
        <Button
          size={isThumbnail ? 'medium' : 'large'}
          cName={`add-to-cart-btn ${isComDomain ? 'com' : ''}`}
          variant="outlined"
          disabled={createNotifyMeLoading}
          onClick={handleClickNotifyMeButton}
        >
          Notify Me
        </Button>
      )}
      {stateOfVariant === 'addToCart' && (
        <>
          <Button
            size={isThumbnail ? 'medium' : 'large'}
            cName={`add-to-cart-btn add-bag ${isComDomain ? 'com' : ''}`}
            variant="outlined"
            disabled={productTypeId === 5 || cartLinesAddLoading}
            onClick={(e) => handleClickAddToCartButton(e, false)}
          >
            Add To Bag
          </Button>
          <Button
            size={isThumbnail ? 'medium' : 'large'}
            cName={`add-to-cart-btn buy-now ${isComDomain ? 'com' : ''}`}
            variant="outlined"
            disabled={productTypeId === 5 || cartLinesAddLoading}
            onClick={(e) => handleClickAddToCartButton(e, true)}
          >
            Buy Now
          </Button>
        </>
      )}

      {isOTPVerificationModalOpen && (
        <OTPVerificationModal open={isOTPVerificationModalOpen} onClose={handleOTPVerificationModalClose} />
      )}
    </div>
  );
};

export default DesktopDetailAddToCartButton;
